package com.klantenstop.web.utils

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import com.klantenstop.web.data.models.domain.Company
import com.klantenstop.web.data.models.domain.ReviewTotal
import com.klantenstop.web.navigation.Navigation
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLLinkElement
import org.w3c.dom.HTMLMetaElement
import org.w3c.dom.HTMLScriptElement

// Declared as external so the compiler delegates directly to the browser's
// built-in encodeURIComponent at runtime without inlining or mangling.
private external fun encodeURIComponent(component: String): String

/**
 * Returns the canonical site origin.
 * Prefers the build-time SITE_URL global (correct during static export and in production).
 * Falls back to window.location.origin so local dev without the env var still works.
 */
private val siteOrigin: String
    get() = SiteGlobals.baseUrl.takeIf { it.isNotBlank() } ?: window.location.origin

/** True only when running in the production environment (set at build time via ENVIRONMENT env var). */
private fun isProduction(): Boolean = SiteGlobals.environment == "production"

private val defaultOgImage get() = "$siteOrigin/images/logo_mail_header.png"

/** Facebook App ID used for the `fb:app_id` Open Graph tag (links shares to Facebook Insights). */
private const val FACEBOOK_APP_ID = "1807695473723716"

private val defaultSeoKeywords = listOf(
    "klantenstop",
    "klanten stop",
    "patiëntenstop",
    "patiënten stop",
    "wachtlijst",
    "wachtlijsten",
    "huisarts patiëntenstop",
    "tandarts patiëntenstop",
    "zorgverlener wachtlijst",
)

private const val META_DESCRIPTION_MAX_LEN = 120

/**
 * Primary site navigation, exposed to search engines as `SiteNavigationElement` structured data
 * (see [injectWebsiteJsonLd]). This is a hint, not a guarantee, that Google may use these as
 * sitelinks under the main branded search result. Mirrors the indexable links surfaced in
 * [com.klantenstop.web.components.LandingHeader]'s `NavigationItems` plus the search page, since
 * structured data should reflect real, crawlable site navigation rather than invent new links.
 */
private val primaryNavigationLinks: List<Pair<String, String>> = listOf(
    "Home" to Navigation.Screen.Home.route,
    "Zoeken" to Navigation.Screen.Search().route.substringBefore("?"),
    "Patiëntenstop" to Navigation.Screen.PatientStopWaitlist.route,
    "Over Ons" to Navigation.Screen.About.route,
    "Contact" to Navigation.Screen.Contact.route,
)

/** Rating scale used sitewide for company reviews (1 to 5 stars). */
private const val RATING_BEST = "5"
private const val RATING_WORST = "1"

/**
 * A single crumb in a `BreadcrumbList` JSON-LD graph.
 * [url] is optional — schema.org/Google only require it for non-final entries.
 */
internal data class BreadcrumbItem(
    val name: String,
    val url: String? = null,
)

/**
 * Explicit, extendable lookup from (a substring of) a company's category names to the
 * most specific applicable schema.org `LocalBusiness` sub-type. Matching is case-insensitive and
 * checks every category; list order defines precedence, independent of category order. Falls back to generic `LocalBusiness`
 * when no entry matches, so unmapped/new categories never break structured data.
 */
private val categorySchemaTypeKeywords: List<Pair<String, String>> = listOf(
    "tandarts" to "Dentist",
    "orthodontist" to "Dentist",
    "huisarts" to "Physician",
    "dokter" to "Physician",
    "dermatoloog" to "Physician",
    "dierenarts" to "VeterinaryCare",
    "kapper" to "HairSalon",
    "kapsalon" to "HairSalon",
    "nagelstyl" to "NailSalon",
    "nagelstudio" to "NailSalon",
    "schoonheidsspecialist" to "BeautySalon",
    "schoonheidssalon" to "BeautySalon",
)

/**
 * Resolves the most specific schema.org `LocalBusiness` sub-type for [company], based on all its
 * assigned categories (list order defines precedence). Falls back to generic `LocalBusiness` for unmapped/uncategorized companies.
 */
internal fun schemaLocalBusinessType(company: Company): String {
    val categoryNames = company.categories.map { it.name.lowercase() }
    return categorySchemaTypeKeywords.firstOrNull { (keyword, _) -> categoryNames.any { it.contains(keyword) } }?.second
        ?: "LocalBusiness"
}

/**
 * Updates document <head> meta tags for SEO on every page.
 * Should be called from PageLayout with the current page's title and description.
 * When [indexable] is false, OR when not running on the production hostname,
 * a noindex,nofollow robots tag is set to prevent unintended indexing of dev/staging.
 */
@Composable
fun UpdatePageSeo(
    title: String,
    description: String,
    canonicalPath: String? = null,
    ogType: String = "website",
    indexable: Boolean = true,
    keywords: List<String> = emptyList(),
    breadcrumbLabel: String? = null,
) {
    LaunchedEffect(title, description, canonicalPath, ogType, indexable, keywords, breadcrumbLabel) {
        val fullTitle = if (title.contains("KlantenStop")) title else "$title | KlantenStop"
        val canonicalUrl = canonicalPath?.let { "$siteOrigin$it" }
        val mergedKeywords = (defaultSeoKeywords + keywords)
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .distinct()

        setMetaTagByName("robots", if (indexable && isProduction()) "index,follow" else "noindex,nofollow")
        setMetaTagByName("description", description)
        setMetaTagByName("keywords", mergedKeywords.joinToString(", "))

        setMetaTagByProperty("og:title", fullTitle)
        setMetaTagByProperty("og:description", description)
        setMetaTagByProperty("og:type", ogType)
        setMetaTagByProperty("og:locale", "nl_BE")
        setMetaTagByProperty("og:site_name", "KlantenStop")
        setMetaTagByProperty("og:image", defaultOgImage)
        setMetaTagByProperty("fb:app_id", FACEBOOK_APP_ID)
        if (canonicalUrl != null) {
            setMetaTagByProperty("og:url", canonicalUrl)
            setCanonicalLink(canonicalUrl)
        }

        setMetaTagByName("twitter:card", "summary")
        setMetaTagByName("twitter:title", fullTitle)
        setMetaTagByName("twitter:description", description)

        if (indexable) {
            injectWebsiteJsonLd(canonicalUrl ?: siteOrigin)

            if (breadcrumbLabel != null && canonicalUrl != null) {
                injectBreadcrumbListJsonLd(
                    listOf(
                        BreadcrumbItem(name = "Home", url = siteOrigin),
                        BreadcrumbItem(name = breadcrumbLabel, url = canonicalUrl),
                    )
                )
            } else {
                removeBreadcrumbListJsonLd()
            }
        } else {
            removeWebsiteJsonLd()
            removeBreadcrumbListJsonLd()
        }
    }
}

/**
 * Updates SEO meta tags and injects JSON-LD structured data for a company detail page.
 * Handles all states: loading (company == null, !isNotFound), not-found, and loaded.
 * Removes the JSON-LD script element when the composable leaves composition.
 */
@Composable
fun UpdateCompanySeo(
    company: Company?,
    reviewTotal: ReviewTotal,
    companyIdentifier: String,
    isNotFound: Boolean = false,
) {
    // Remove the JSON-LD script elements when navigating away from the company page entirely.
    DisposableEffect(Unit) {
        onDispose {
            removeCompanyJsonLd()
            removeBreadcrumbListJsonLd()
        }
    }

    LaunchedEffect(company?.id, company?.publicIdentifier, reviewTotal.total, reviewTotal.average, companyIdentifier, isNotFound) {
        val canonicalIdentifier = company?.publicIdentifier ?: companyIdentifier
        val canonicalUrl = "$siteOrigin${Navigation.Screen.CompanyDetail(encodeURIComponent(canonicalIdentifier)).route}"

        if (company == null) {
            // Loading or 404 — reset tags so nothing stale bleeds in from a previous page.
            val title = if (isNotFound) "Bedrijf niet gevonden | KlantenStop" else "KlantenStop"
            val description = if (isNotFound)
                "Dit bedrijf kon niet worden gevonden op KlantenStop."
            else
                "KlantenStop — vind snel welke dienstverleners nog nieuwe klanten accepteren."

            setMetaTagByName("robots", "noindex,nofollow")
            setMetaTagByName("description", description)
            setMetaTagByProperty("og:title", title)
            setMetaTagByProperty("og:description", description)
            setMetaTagByProperty("og:type", "website")
            setMetaTagByProperty("og:locale", "nl_BE")
            setMetaTagByProperty("og:site_name", "KlantenStop")
            setMetaTagByProperty("og:url", canonicalUrl)
            setMetaTagByProperty("og:image", defaultOgImage)
            setMetaTagByProperty("fb:app_id", FACEBOOK_APP_ID)
            setCanonicalLink(canonicalUrl)
            setMetaTagByName("twitter:card", "summary")
            setMetaTagByName("twitter:title", title)
            setMetaTagByName("twitter:description", description)
            removeCompanyJsonLd()
            removeBreadcrumbListJsonLd()
            return@LaunchedEffect
        }

        val statusText = company.status.status.toText()
        val city = company.address?.city
        val description = buildString {
            append(company.name)
            if (city != null) append(" in $city")
            append(" — $statusText.")
            val body = company.description.trim()
            if (body.isNotBlank()) {
                append(" ")
                append(body.take(META_DESCRIPTION_MAX_LEN))
                if (body.length > META_DESCRIPTION_MAX_LEN) append("…")
            } else {
                append(" Bekijk de klantenstop status en beoordelingen op KlantenStop.")
            }
        }

        val title = "${company.name} | KlantenStop"

        setMetaTagByName("robots", if (isProduction()) "index,follow" else "noindex,nofollow")
        setMetaTagByName("description", description)

        setMetaTagByProperty("og:title", title)
        setMetaTagByProperty("og:description", description)
        setMetaTagByProperty("og:type", "business.business")
        setMetaTagByProperty("og:locale", "nl_BE")
        setMetaTagByProperty("og:site_name", "KlantenStop")
        setMetaTagByProperty("og:url", canonicalUrl)
        setMetaTagByProperty("og:image", company.logo?.url ?: defaultOgImage)
        setMetaTagByProperty("fb:app_id", FACEBOOK_APP_ID)
        setCanonicalLink(canonicalUrl)

        setMetaTagByName("twitter:card", "summary")
        setMetaTagByName("twitter:title", title)
        setMetaTagByName("twitter:description", description)

        injectCompanyJsonLd(company, reviewTotal, canonicalUrl)
        injectBreadcrumbListJsonLd(
            listOf(
                BreadcrumbItem(name = "Home", url = siteOrigin),
                BreadcrumbItem(name = "Zoeken", url = "$siteOrigin/zoeken"),
                BreadcrumbItem(name = company.name, url = canonicalUrl),
            )
        )
    }
}

/**
 * Injects an `ItemList` JSON-LD script for a search results page (mirrors the backend's bot-facing
 * `ItemList`). Googlebot renders the SPA instead of being routed to the backend, so this is what it indexes.
 * The script is removed when the page leaves composition or while there are no results.
 */
@Composable
fun UpdateSearchItemListJsonLd(
    heading: String?,
    canonicalPath: String,
    companies: List<Company>,
    total: Long,
    page: Int,
    pageSize: Int,
) {
    DisposableEffect(Unit) {
        onDispose { removeSearchItemListJsonLd() }
    }

    LaunchedEffect(heading, canonicalPath, companies, total, page, pageSize) {
        if (heading == null || companies.isEmpty()) {
            removeSearchItemListJsonLd()
            return@LaunchedEffect
        }
        injectSearchItemListJsonLd(heading, "$siteOrigin$canonicalPath", companies, total, page, pageSize)
    }
}

internal fun removeSearchItemListJsonLd() {
    document.getElementById("search-itemlist-jsonld")?.let { it.parentNode?.removeChild(it) }
}

internal fun injectSearchItemListJsonLd(
    heading: String,
    canonicalUrl: String,
    companies: List<Company>,
    total: Long,
    page: Int,
    pageSize: Int,
) {
    removeSearchItemListJsonLd()

    val firstPosition = (page - 1) * pageSize
    val items = companies.mapIndexed { index, company ->
        val companyUrl = "$siteOrigin${Navigation.Screen.CompanyDetail(encodeURIComponent(company.publicIdentifier)).route}"
        val address = company.address
        val addressFields = if (address != null) listOf(
            "streetAddress" to "${address.streetName} ${address.streetNumber}".trim(),
            "addressLocality" to address.city,
            "postalCode" to address.postalCode,
        ).filter { it.second.isNotBlank() } else emptyList()
        val addressBlock = if (addressFields.isNotEmpty())
            ""","address": {"@type": "PostalAddress", ${addressFields.joinToString(", ") { (key, value) -> "\"$key\": \"${value.escapeJson()}\"" }}}"""
        else ""
        val phoneBlock = if (company.phoneNumber.isNotBlank())
            ""","telephone": "${company.phoneNumber.trim().escapeJson()}"""" else ""
        """
        {
            "@type": "ListItem",
            "position": ${firstPosition + index + 1},
            "item": {
                "@type": "LocalBusiness",
                "@id": "${companyUrl.escapeJson()}",
                "name": "${company.name.escapeJson()}",
                "url": "${companyUrl.escapeJson()}"$addressBlock$phoneBlock
            }
        }
        """.trimIndent()
    }

    val jsonLd = """
        {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "${heading.escapeJson()}",
            "url": "${canonicalUrl.escapeJson()}",
            "numberOfItems": $total,
            "itemListElement": [${items.joinToString(",")}]
        }
    """.trimIndent()

    val script = document.createElement("script") as HTMLScriptElement
    script.id = "search-itemlist-jsonld"
    script.setAttribute("type", "application/ld+json")
    script.textContent = jsonLd
    document.head?.appendChild(script)
}

internal fun removeCompanyJsonLd() {
    document.getElementById("company-jsonld")?.let { it.parentNode?.removeChild(it) }
}

private fun removeWebsiteJsonLd() {
    document.getElementById("website-jsonld")?.let { it.parentNode?.removeChild(it) }
}

internal fun removeBreadcrumbListJsonLd() {
    document.getElementById("breadcrumb-jsonld")?.let { it.parentNode?.removeChild(it) }
}

/**
 * Injects a `BreadcrumbList` JSON-LD script representing the user-meaningful navigation path
 * to the current page (e.g. Home › Zoeken › Company Name). Per schema.org/Google, [item] (the
 * crumb's URL) is only required for non-final entries — [BreadcrumbItem.url] may be null for the
 * last entry.
 */
internal fun injectBreadcrumbListJsonLd(breadcrumbs: List<BreadcrumbItem>) {
    removeBreadcrumbListJsonLd()

    val itemListElement = breadcrumbs.mapIndexed { index, crumb ->
        val itemField = crumb.url?.let { ""","item": "${it.escapeJson()}"""" } ?: ""
        """
        {
            "@type": "ListItem",
            "position": ${index + 1},
            "name": "${crumb.name.escapeJson()}"
            $itemField
        }
        """.trimIndent()
    }.joinToString(",")

    val jsonLd = """
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                $itemListElement
            ]
        }
    """.trimIndent()

    val script = document.createElement("script") as HTMLScriptElement
    script.id = "breadcrumb-jsonld"
    script.setAttribute("type", "application/ld+json")
    script.textContent = jsonLd
    document.head?.appendChild(script)
}

private fun injectWebsiteJsonLd(currentUrl: String) {
    removeWebsiteJsonLd()

    val siteNavigationElements = primaryNavigationLinks.mapIndexed { index, (name, path) ->
        """
        {
            "@type": "SiteNavigationElement",
            "position": ${index + 1},
            "name": "${name.escapeJson()}",
            "url": "${(siteOrigin + path).escapeJson()}"
        }
        """.trimIndent()
    }.joinToString(",")

    val jsonLd = """
        {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "WebSite",
                    "@id": "${siteOrigin.escapeJson()}#website",
                    "url": "${siteOrigin.escapeJson()}",
                    "name": "KlantenStop",
                    "alternateName": [
                        "Klanten Stop",
                        "Patiëntenstop",
                        "Patiënten stop",
                        "Wachtlijst platform"
                    ],
                    "inLanguage": "nl-BE",
                    "description": "Zoek Belgische dienstverleners en zorgverleners zonder klantenstop of patiëntenstop."
                },
                {
                    "@type": "Organization",
                    "@id": "${siteOrigin.escapeJson()}#organization",
                    "name": "KlantenStop",
                    "url": "${siteOrigin.escapeJson()}",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "${defaultOgImage.escapeJson()}"
                    },
                    "sameAs": ["${Constants.FACEBOOK_URL.escapeJson()}"],
                    "description": "KlantenStop helpt gebruikers Belgische wachtlijsten en patiëntenstops sneller te omzeilen."
                },
                $siteNavigationElements,
                {
                    "@type": "WebPage",
                    "@id": "${currentUrl.escapeJson()}#webpage",
                    "url": "${currentUrl.escapeJson()}",
                    "name": "KlantenStop",
                    "inLanguage": "nl-BE",
                    "isPartOf": {
                        "@id": "${siteOrigin.escapeJson()}#website"
                    },
                    "about": [
                        "klantenstop",
                        "patiëntenstop",
                        "wachtlijst",
                        "zorgverlener beschikbaarheid"
                    ]
                }
            ]
        }
    """.trimIndent()

    val script = document.createElement("script") as HTMLScriptElement
    script.id = "website-jsonld"
    script.setAttribute("type", "application/ld+json")
    script.textContent = jsonLd
    document.head?.appendChild(script)
}

internal fun injectCompanyJsonLd(company: Company, reviewTotal: ReviewTotal, canonicalUrl: String) {
    removeCompanyJsonLd()

    val address = company.address
    val addressBlock = if (address != null) """
        ,"address": {
            "@type": "PostalAddress",
            "streetAddress": "${address.streetName.escapeJson()} ${address.streetNumber.escapeJson()}",
            "addressLocality": "${address.city.escapeJson()}",
            "postalCode": "${address.postalCode.escapeJson()}",
            "addressCountry": "${address.country.escapeJson()}"
        }""" else ""

    // Only emit geo coordinates when they've actually been set — the domain model defaults
    // latitude/longitude to 0.0 (Gulf of Guinea), which would otherwise misrepresent the business.
    val geoBlock = address?.coordinates?.takeIf { it.latitude != 0.0 || it.longitude != 0.0 }?.let { coordinates -> """
        ,"geo": {
            "@type": "GeoCoordinates",
            "latitude": ${coordinates.latitude},
            "longitude": ${coordinates.longitude}
        }"""
    } ?: ""

    val phoneBlock = if (company.phoneNumber.isNotBlank())
        ""","telephone": "${company.phoneNumber.escapeJson()}"""" else ""

    // schema.org `url` must be the canonical page; the business's own site belongs in `sameAs`
    val emailBlock = if (company.emailAddress.isNotBlank())
        ""","email": "${company.emailAddress.trim().escapeJson()}"""" else ""

    // Category names, mirroring the backend's bot-facing JSON-LD
    val keywordsBlock = company.categories.map { it.name }.filter { it.isNotBlank() }.takeIf { it.isNotEmpty() }
        ?.let { names -> ""","keywords": "${names.joinToString(", ").escapeJson()}"""" } ?: ""

    val websiteBlock = if (company.website != null)
        ""","sameAs": "${company.website.toString().escapeJson()}"""" else ""

    // Reuses the same image already used for og:image, falling back to the sitewide logo.
    val imageBlock = ""","image": "${(company.logo?.url ?: defaultOgImage).escapeJson()}""""

    val ratingBlock = if (reviewTotal.total > 0) """
        ,"aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "${reviewTotal.average}",
            "reviewCount": "${reviewTotal.total}",
            "bestRating": "$RATING_BEST",
            "worstRating": "$RATING_WORST"
        }""" else ""

    val jsonLd = """
        {
            "@context": "https://schema.org",
            "@type": "${schemaLocalBusinessType(company).escapeJson()}",
            "@id": "${canonicalUrl.escapeJson()}",
            "name": "${company.name.escapeJson()}",
            "url": "${canonicalUrl.escapeJson()}",
            "description": "${company.description.trim().escapeJson()}"
            $addressBlock
            $geoBlock
            $phoneBlock
            $emailBlock
            $keywordsBlock
            $websiteBlock
            $imageBlock
            $ratingBlock
        }
    """.trimIndent()

    val script = document.createElement("script") as HTMLScriptElement
    script.id = "company-jsonld"
    script.setAttribute("type", "application/ld+json")
    script.textContent = jsonLd
    document.head?.appendChild(script)
}

/**
 * Escapes a string for safe embedding inside a JSON string value.
 * Covers the full set of characters required by the JSON spec (RFC 8259):
 * backslash, double-quote, and all control characters U+0000–U+001F.
 */
private fun String.escapeJson(): String = buildString {
    for (ch in this@escapeJson) {
        when (ch) {
            '\\' -> append("\\\\")
            '"' -> append("\\\"")
            '\n' -> append("\\n")
            '\r' -> append("\\r")
            '\t' -> append("\\t")
            '\b' -> append("\\b")
            '\u000C' -> append("\\f") // form feed
            else -> if (ch.code < 0x20) {
                // Remaining control characters as \uXXXX
                append("\\u${ch.code.toString(16).padStart(4, '0')}")
            } else {
                append(ch)
            }
        }
    }
}

private fun setMetaTagByName(name: String, content: String) {
    val existing = document.querySelector("meta[name='$name']") as? HTMLMetaElement
    if (existing != null) {
        existing.content = content
    } else {
        val meta = document.createElement("meta") as HTMLMetaElement
        meta.setAttribute("name", name)
        meta.content = content
        document.head?.appendChild(meta)
    }
}

private fun setMetaTagByProperty(property: String, content: String) {
    val existing = document.querySelector("meta[property='$property']") as? HTMLMetaElement
    if (existing != null) {
        existing.content = content
    } else {
        val meta = document.createElement("meta") as HTMLMetaElement
        meta.setAttribute("property", property)
        meta.content = content
        document.head?.appendChild(meta)
    }
}

private fun setCanonicalLink(url: String) {
    val existing = document.querySelector("link[rel='canonical']") as? HTMLLinkElement
    if (existing != null) {
        existing.href = url
    } else {
        val link = document.createElement("link") as HTMLLinkElement
        link.rel = "canonical"
        link.href = url
        document.head?.appendChild(link)
    }
}
